import { plainToClass } from 'class-transformer';
import { inject, injectable } from 'inversify';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { defer, Observable, ReplaySubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { Logger } from '../models/logger';
import { RouteProps } from '../types/route-types';
import { FeatureFlags, UserSettings } from '../utils/request.utils';
import { rememberUploadExpirySettings } from '../utils/upload-expiry';
import { ApiConfig } from './api-config';
import { LightHttp } from './light-http.service';

export type FeatureFlagsEmission = { flags: FeatureFlags; source: 'props' | 'api' };

@injectable()
export class SettingsService {
    private readonly logger = Logger.getLogger('SettingsService');

    private _motivesEnabled = true;
    private _serverSettingsLoaded = false;
    private _userSettings = new ReplaySubject<UserSettings>(1);
    /**
     * Site-wide feature flags. Emitted as its own observable so call sites that only
     * care about flags don't have to thread through a UserSettings object that, as of
     * 2026-05, no longer carries them. Both subjects are emitted in lock-step from a
     * single /v1/api/settings response.
     *
     * Emission provenance (2026-09-02, LEDGER 02:29(1)/02:52(1)). `xconvert-web-ssr`'s
     * routes/utils.ts:8 hardcodes the PROD api host, so every build - QA included - bakes
     * PROD's flag set into props.settings. That makes the first emission a real `false`,
     * not `undefined`, and a gate written as `flag === undefined ? placeholder : ...`
     * renders "coming soon" on every load and permanently when the API call fails.
     * So the subject carries WHERE the value came from, and the mount gate keys on that.
     */
    private _featureFlags = new ReplaySubject<FeatureFlagsEmission>(1);
    get userSettings(): Observable<UserSettings> {
        return this._userSettings.asObservable();
    }
    /** Unchanged shape for the ~dozen existing consumers: bare flags, no provenance. */
    get featureFlags(): Observable<FeatureFlags> {
        return this._featureFlags.asObservable().pipe(map(e => e.flags));
    }
    /** The mount gate's ONLY input. Subscribe for the page's lifetime - never `take(1)`. */
    readonly featureFlags$: Observable<FeatureFlagsEmission> = this._featureFlags.asObservable();

    readonly userToggleShowMotives = new ReplaySubject<boolean>(1);

    constructor(
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(LightHttp) private http: LightHttp,
    ) {
    }

    initializeFromProps(props: RouteProps) {
        // Don't overwrite server settings with SSR settings if API already responded
        if (this._serverSettingsLoaded) {
            return;
        }
        const settings = (props.settings as any) || {};
        this.emitSplit(settings, 'props');
    }

    /**
     * Split the flat /v1/api/settings JSON into UserSettings + FeatureFlags
     * and emit each on its own subject. Wire format puts featureFlags as a
     * top-level sibling of UserSettings fields (see SettingsApiService.kt on
     * the backend); we strip it out here so UserSettings no longer carries
     * it. Returns the materialised UserSettings for callers that need it.
     */
    private emitSplit(raw: any, source: 'props' | 'api'): UserSettings {
        const { featureFlags, ...userSettingsFields } = raw;
        // FIX-2 (C3): the expired-upload minutes, readable by flag subscribers (recorded before the flags emit).
        rememberUploadExpirySettings(userSettingsFields);
        const userSettings = plainToClass(UserSettings, userSettingsFields);
        const flags = featureFlags ? plainToClass(FeatureFlags, featureFlags) : new FeatureFlags();
        this._userSettings.next(userSettings);
        this._featureFlags.next({ flags, source });
        return userSettings;
    }

    public initialize(): Observable<UserSettings> {
        // defer() so each subscribe (including retry()) issues a fresh HTTP call.
        // Without defer, retry() re-subscribes to the same captured response$
        // which doesn't re-execute the request in jest mocks (and is brittle in
        // production for any cold-observable implementation that caches).
        return defer(() => this.http.get<any>(this.apiConfig.getUserSettingsEndpoint())).pipe(
            map(raw => this.emitSplit(raw, 'api')),
            tap({
                next: () => {
                    this._serverSettingsLoaded = true;
                    // DO NOT call this._userSettings.complete() here.
                    //
                    // Why: _userSettings is a ReplaySubject(1) consumed via zip()
                    // in connectivity.service.ts and via late subscribers across
                    // the app. Completing it kills zip operators after the first
                    // pair (zip completes when ANY input completes) and prevents
                    // any future emissions to existing or future subscribers.
                    // The ReplaySubject's buffer (size 1) already replays the
                    // latest value to any new subscriber, so completion adds
                    // nothing useful and silently breaks consumers.
                    //
                    // Reverting this comment / re-adding complete() will fail
                    // settings.service.spec.ts:'should NOT complete the userSettings
                    // subject (kept open for late subscribers)' and reintroduce
                    // the zip-breaks-after-init bug fixed in commit 3cbb667d.
                },
                error: err => {
                    this.logger.error({ message: 'Failed to get user settings.', error: err });
                    // DO NOT call this._userSettings.error(err) here.
                    //
                    // Why: erroring the ReplaySubject permanently terminates it
                    // in an error state. Any existing subscriber's error handler
                    // fires, any future subscriber gets the cached error
                    // immediately, and the caller's retry strategy CANNOT
                    // recover because the subject is dead. On a transient
                    // network failure (offline blip, DNS hiccup), this would
                    // break user-settings access for the rest of the session.
                    //
                    // Instead: the error propagates through the returned
                    // observable so the caller (typically _app.tsx with a
                    // retry pipe) can decide how to react. Subscribers keep
                    // receiving the SSR settings already in the buffer.
                    //
                    // Reverting this comment / re-adding .error() will fail
                    // settings.service.spec.ts:'should NOT error the userSettings
                    // subject on API failure (keeps SSR settings for subscribers)'
                    // and reintroduce the permanent-subject-death bug fixed in
                    // commit 3cbb667d.
                },
            }),
        );
    }

    get motivesEnabled() {
        return this._motivesEnabled;
    }

    set motivesEnabled(_motivesEnabled: boolean) {
        this._motivesEnabled = _motivesEnabled;
        this.userToggleShowMotives.next(this._motivesEnabled);
    }

    get onUserToggleShowMotives() {
        return this.userToggleShowMotives.asObservable();
    }

}
